var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PeriodAccumulationConfig_1;
import { ColumnTypeEnum, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import './sortColumn'; // force import for JsonSchemaV2
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { getRenamedColumns } from './toolConfigUtil';
import { periodAccumulationConfigSchemaValidation } from '../validations.generated';
export const periodAccumulationConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:PeriodAccumulationConfig',
    type: 'object',
    properties: {
        valueColumns: {
            type: 'array',
            items: {
                oneOf: [
                    {
                        $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                    },
                    { type: 'string', minLength: 1 }
                ]
            },
            uniqueItems: true,
            minItems: 1
        },
        groupColumns: {
            type: 'array',
            items: {
                oneOf: [
                    {
                        $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                    },
                    { type: 'string', minLength: 1 }
                ]
            },
            uniqueItems: true
        },
        orderColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:SortColumn' },
            uniqueItemProperties: ['name'],
            minItems: 1
        },
        accumulationLevel: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:CalendarToolVariable'
                },
                { type: 'string' }
            ]
        }
    },
    required: ['valueColumns', 'groupColumns', 'orderColumns']
};
let PeriodAccumulationConfig = PeriodAccumulationConfig_1 = class PeriodAccumulationConfig extends ToolConfig {
    constructor(valueColumns, groupColumns, orderColumns, accumulationLevel, customFormats) {
        super(ToolType.PERIOD_ACCUMULATION, customFormats);
        this.valueColumns = valueColumns;
        this.groupColumns = groupColumns;
        this.orderColumns = orderColumns;
        if (accumulationLevel)
            this.accumulationLevel = accumulationLevel;
        JSON.validate(this, PeriodAccumulationConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return PeriodAccumulationConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return PeriodAccumulationConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return PeriodAccumulationConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return PeriodAccumulationConfig_1.outs;
    }
    static get in() {
        return PeriodAccumulationConfig_1.ins[0];
    }
    static get out() {
        return PeriodAccumulationConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.PERIOD_ACCUMULATION.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['period', 'date', 'incentives', 'cumulative sum', 'running total', 'accumulate'], true);
    }
    applyParameters(parameters) {
        return new PeriodAccumulationConfig_1(parameters.getStrArrayVals(this.valueColumns), parameters.getStrArrayVals(this.groupColumns), this.orderColumns.map((col) => col.applyParameters(parameters)), this.accumulationLevel ? parameters.getCalLevelVal(this.accumulationLevel) : undefined, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            valueColumns: parameters.getStrArrayVals(this.valueColumns),
            groupColumns: parameters.getStrArrayVals(this.groupColumns),
            orderColumns: this.orderColumns.map((col) => col.getBaseValues(parameters)),
            accumulationLevel: this.accumulationLevel ? parameters.getCalLevelVal(this.accumulationLevel) : undefined
        };
    }
    static getOutputColumn(source) {
        return `${source} Period Accumulation`;
    }
    getOutput(iConfigHelper, ins) {
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        const { valueColumns, groupColumns } = baseVals;
        return this.getOutputForRunningConfig(iConfigHelper, ins, this.in, this.out, valueColumns, groupColumns, this.orderColumns, PeriodAccumulationConfig_1.getOutputColumn);
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const { valueColumns, groupColumns, orderColumns } = this.getBaseValues(iConfigHelper.getParameters());
        const inputs = new Map();
        const outputs = [];
        // TBD product design
        // // the same col can't be in both groupColumns and orderColumns
        // if (orderColumns.some((c) => groupColumns.includes(c.name))) {
        // 	return ToolValidationResult.getInvalidResult(
        // 		'The same column cannot be used as both a group and an order column'
        // 	);
        // }
        const columnNames = source.map((col) => col.name);
        valueColumns.forEach((vc) => {
            inputs.set(vc, ['number', 'decimal']);
            outputs.push(getRenamedColumns(columnNames, PeriodAccumulationConfig_1.getOutputColumn(vc), ToolType.PERIOD_ACCUMULATION));
        });
        groupColumns.forEach((c) => {
            if (!inputs.has(c)) {
                inputs.set(c, []);
            }
        });
        // only one period column may be selected for grouping
        const groupByPeriodCols = groupColumns.filter((c) => source.filter((col) => col.type === ColumnTypeEnum.period).some((col) => col.name === c));
        if (groupByPeriodCols.length > 1) {
            return ToolValidationResult.getInvalidResult('Only one period column is allowed in group columns.');
        }
        orderColumns.forEach((c) => {
            if (!inputs.has(c.name)) {
                inputs.set(c.name, []);
            }
        });
        const sourceValidationResult = validateSourceWithDetail(source, inputs, outputs);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
PeriodAccumulationConfig = PeriodAccumulationConfig_1 = __decorate([
    JsonSchemaV2(periodAccumulationConfigSchema, periodAccumulationConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.PERIOD_ACCUMULATION)
], PeriodAccumulationConfig);
export { PeriodAccumulationConfig };
//# sourceMappingURL=periodAccumulationConfig.js.map